/**
 * ArticleInventoryScanPage (Jul-2026, User-Wunsch) — mobile, LOGIN-FREIE
 * Inventur-Erfassung, per QR aus der Warenwirtschaft gestartet.
 * Ablauf: Lagerort scannen/wählen → Artikel-Barcode scannen → Menge zählen →
 * optional Fotos → buchen (nutzt die bestehende GoBD-Inventur-Buchung).
 * Route: /inventur-scan/:token
 */
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ScanLine, MapPin, Check, AlertCircle, Camera, X, Package } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import EanCameraScanner from "../components/wms/EanCameraScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 6;

export default function ArticleInventoryScanPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [location, setLocation] = useState(null); // {id, code, name}
    const [article, setArticle] = useState(null);
    const [qty, setQty] = useState("");
    const [photos, setPhotos] = useState([]); // [{path}]
    const [scanMode, setScanMode] = useState(null); // "location" | "article"
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [booked, setBooked] = useState(0);
    const [manualCode, setManualCode] = useState("");

    const loadInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/public/inventory-scan/${token}`);
            setInfo(data); setBooked(data.booked_count || 0);
        } catch (e) {
            setError(e.response?.data?.detail || "Inventur-Sitzung ungültig oder abgelaufen.");
        } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { loadInfo(); }, [loadInfo]);

    const resolveLocation = async (payload) => {
        setScanMode(null);
        try {
            const { data } = await axios.get(`${API}/public/inventory-scan/${token}/resolve-location`, { params: { payload } });
            setLocation(data); toast.success(`Lagerort: ${data.code}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Lagerort nicht gefunden"); }
    };

    const findArticle = async (code) => {
        setScanMode(null);
        const c = (code || "").trim();
        if (!c) return;
        try {
            const { data } = await axios.get(`${API}/public/inventory-scan/${token}/article`, { params: { code: c } });
            setArticle(data);
            setQty(String(data.stock_qty ?? ""));
            setPhotos((data.photo_paths || []).map((p) => ({ path: p })).slice(0, MAX_PHOTOS));
            toast.success(`${data.name} (Bestand ${data.stock_qty ?? 0})`);
        } catch (e) { toast.error(e.response?.data?.detail || "Artikel nicht gefunden"); }
    };

    const onPhoto = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (photos.length >= MAX_PHOTOS) { toast.error(`Max. ${MAX_PHOTOS} Fotos`); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/public/inventory-scan/${token}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPhotos((p) => [...p, { path: data.path }]);
            toast.success("Foto hochgeladen");
        } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
        setUploading(false);
    };

    const book = async () => {
        if (!location) { toast.error("Bitte Lagerort scannen/wählen"); return; }
        if (!article) { toast.error("Bitte Artikel scannen"); return; }
        if (qty === "" || isNaN(Number(qty))) { toast.error("Bitte Menge eingeben"); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/public/inventory-scan/${token}/book`, {
                article_id: article.id, location_id: location.id,
                counted_qty: Number(qty), photo_paths: photos.map((p) => p.path),
            });
            toast.success(`Gebucht: ${data.name} → ${data.qty_after} (${data.delta >= 0 ? "+" : ""}${data.delta})`);
            setBooked((b) => b + 1);
            setArticle(null); setQty(""); setPhotos([]); setManualCode("");
        } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
        setBusy(false);
    };

    if (loading) return <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Lädt…</div>;
    if (error) return (
        <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center px-4">
            <div className="bg-white rounded-sm p-6 max-w-sm text-center" data-testid="invscan-error">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="font-bold text-[#111827]">Nicht verfügbar</p>
                <p className="text-sm text-gray-600 mt-1">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50" data-testid="inventory-scan-page">
            <div className="bg-[#111827] text-white sticky top-0 z-30">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-2">
                    <ScanLine className="w-5 h-5 text-[#FFCC00]" />
                    <div className="min-w-0">
                        <div className="font-display font-black text-lg leading-tight">Inventur erfassen</div>
                        <div className="text-[11px] text-gray-400 truncate">{info?.dealer_label}</div>
                    </div>
                    <span className="ml-auto text-xs bg-emerald-600 px-2 py-1 rounded-sm font-bold" data-testid="invscan-progress">
                        <Check className="w-3 h-3 inline mr-0.5" />{booked}
                    </span>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
                {/* 1) Lagerort */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> 1. Lagerort</Label>
                    {location ? (
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="invscan-location-set">
                            <Check className="w-4 h-4 text-emerald-600" />
                            <span className="font-bold text-sm">{location.code}{location.name ? ` · ${location.name}` : ""}</span>
                            <button onClick={() => setLocation(null)} className="ml-auto text-gray-400"><X className="w-4 h-4" /></button>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <Button onClick={() => setScanMode("location")} className="flex-1 h-11 bg-[#111827] text-white" data-testid="invscan-scan-location">
                                <ScanLine className="w-4 h-4 mr-1" /> Lagerort-QR scannen
                            </Button>
                            <select
                                className="h-11 border-2 border-gray-300 rounded-sm text-sm px-2 max-w-[45%]"
                                value=""
                                onChange={(e) => { const l = (info?.locations || []).find((x) => x.id === e.target.value); if (l) { setLocation(l); toast.success(`Lagerort: ${l.code}`); } }}
                                data-testid="invscan-location-select"
                            >
                                <option value="">Lagerort wählen…</option>
                                {(info?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ""}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {/* 2) Artikel */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> 2. Artikel</Label>
                    {article ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="invscan-article-set">
                            <div className="font-bold text-sm">{article.name}</div>
                            <div className="text-[11px] text-gray-500">{[article.sku, article.manufacturer].filter(Boolean).join(" · ")} · Bestand {article.stock_qty ?? 0}</div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Button onClick={() => setScanMode("article")} disabled={!location} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invscan-scan-article">
                                <ScanLine className="w-4 h-4 mr-1" /> Artikel-Barcode scannen
                            </Button>
                            <div className="flex gap-2">
                                <Input value={manualCode} onChange={(e) => setManualCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && findArticle(manualCode)} placeholder="oder EAN/SKU eingeben" className="h-10" data-testid="invscan-manual-code" />
                                <Button variant="outline" onClick={() => findArticle(manualCode)} className="h-10 border-2" data-testid="invscan-manual-load">Laden</Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 3) Menge + Fotos + Buchen */}
                {article && (
                    <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-3" data-testid="invscan-count-block">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">3. Gezählte Menge</Label>
                            <Input type="number" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 text-lg font-mono-num" data-testid="invscan-qty" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Fotos ({photos.length}/{MAX_PHOTOS})</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {photos.map((p, i) => (
                                    <div key={i} className="relative">
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.path}`} alt="" className="w-14 h-14 object-cover rounded-sm border" />
                                        <button onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
                                    </div>
                                ))}
                                {photos.length < MAX_PHOTOS && (
                                    <label className="w-14 h-14 border-2 border-dashed border-gray-300 rounded-sm flex items-center justify-center cursor-pointer text-gray-400" data-testid="invscan-photo-add">
                                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
                                    </label>
                                )}
                            </div>
                        </div>
                        <Button onClick={book} disabled={busy} className="w-full h-12 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider" data-testid="invscan-book">
                            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-1" /> Buchen</>}
                        </Button>
                    </div>
                )}
            </div>

            {scanMode && (
                <EanCameraScanner
                    onDetected={(code) => (scanMode === "location" ? resolveLocation(code) : findArticle(code))}
                    onClose={() => setScanMode(null)}
                />
            )}
        </div>
    );
}
